import { formControlClasses, inputClasses } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { HighwayArea } from '@thive/fddm-types';

import { MultiSelectInput } from '@/modules/common/components/inputs/components/MultiSelectInput';
import { getMultiSelectDisplayValue } from '@/modules/common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { SELECTABLE_VEHICLE_AMOUNT_PER_AREA } from '@/modules/vehicles';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { getVehicleTypesByArea } from '@/modules/floorplanDesign/helpers/vehicleType';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';

const containerSx = {
  [`& .${formControlClasses.root} .${inputClasses.root}`]: {
    height: 'auto',
    padding: '4px 12px!important',
  },
};

interface Props {
  selectedHighways: HighwayArea[];
}

const withVehicleType = (highway: HighwayArea, vehicleTypeId: string): HighwayArea => ({
  ...highway,
  highwayPoiSpecs: [
    ...highway.highwayPoiSpecs,
    areaFactory.buildHighwayAreaPoiSpec(highway.id, vehicleTypeId),
  ],
});

/** @description Sets exactly one vehicle type, keeping the existing spec for it when there is one. */
const withOnlyVehicleType = (highway: HighwayArea, vehicleTypeId: string): HighwayArea => {
  const existingSpec = highway.highwayPoiSpecs.find((spec) => spec.vehicleTypeId === vehicleTypeId);

  return {
    ...highway,
    highwayPoiSpecs: [
      existingSpec ?? areaFactory.buildHighwayAreaPoiSpec(highway.id, vehicleTypeId),
    ],
  };
};

/** @description Returns null when removing the vehicle type would leave the highway without any. */
const withoutVehicleType = (highway: HighwayArea, vehicleTypeId: string): HighwayArea | null => {
  const highwayPoiSpecs = highway.highwayPoiSpecs.filter(
    (spec) => spec.vehicleTypeId !== vehicleTypeId,
  );
  if (!isAtLeast1(highwayPoiSpecs)) return null;

  return { ...highway, highwayPoiSpecs };
};

const MultiVehicleTypeComponent = ({ selectedHighways }: Props) => {
  const mode = useAppStore((state) => state.mode);
  const allVehicles = useVehicleStore((state) => state.allVehicles);
  const enabledVehicleTypeIds = useFloorplanDesignStore(
    useShallow((state) => state.getVehicleTypeIds()),
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const snackbarSvc = useMemo(
    () => SnackbarServiceFactory.getInstance().createSnackbarService(),
    [],
  );
  const { t } = useTranslation();
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);

  const enabledVehicles = useMemo(
    () => allVehicles.getByIds(enabledVehicleTypeIds),
    [allVehicles, enabledVehicleTypeIds],
  );

  const options = useMemo(
    () => enabledVehicles.map(({ id, name }) => ({ label: name, value: id })),
    [enabledVehicles],
  );

  const vehicleTypeIdsPerHighway = useMemo(
    () => selectedHighways.map(getVehicleTypesByArea),
    [selectedHighways],
  );

  /** Vehicle types that every selected highway has. */
  const commonVehicleTypeIds = useMemo(() => {
    const [firstIds = [], ...otherIds] = vehicleTypeIdsPerHighway;
    return firstIds.filter((id) => otherIds.every((ids) => ids.includes(id)));
  }, [vehicleTypeIdsPerHighway]);

  const isMixed = vehicleTypeIdsPerHighway.some(
    (ids) => ids.length !== commonVehicleTypeIds.length,
  );
  const selectedOptions = useMemo(
    () => (isMixed ? [] : commonVehicleTypeIds),
    [isMixed, commonVehicleTypeIds],
  );
  const displayValue = getMultiSelectDisplayValue(enabledVehicles, selectedOptions);

  const isDisabled = mode !== 'EDITABLE';

  // Override a mixed selection: every highway gets exactly the chosen vehicle type
  const overrideVehicleType = useCallback(
    async (vehicleTypeId: string) => {
      const confirmed = await confirm(
        t('interface:properties.area.supported_vehicle.override_mixed_popup.label'),
        t('interface:properties.area.supported_vehicle.override_mixed_popup.message', {
          count: selectedHighways.length,
          vehicle: enabledVehicles.find(({ id }) => id === vehicleTypeId)?.name ?? '',
        }),
      );
      if (!confirmed) return;

      const updatedAreas = createEmptyAreas();
      selectedHighways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, withOnlyVehicleType(highway, vehicleTypeId));
      });

      fddmSvc.setPreference('area.vehicleTypeId', vehicleTypeId);
      fddmSvc.updateAreas(updatedAreas);
    },
    [confirm, enabledVehicles, fddmSvc, selectedHighways, t],
  );

  const onItemClick = useCallback(
    (vehicleTypeId: string) => {
      if (isMixed) {
        overrideVehicleType(vehicleTypeId);
        return;
      }

      const updatedAreas = createEmptyAreas();

      if (selectedOptions.includes(vehicleTypeId)) {
        // Deselect on every highway, but only if none of them ends up without a vehicle type
        const updatedHighways = selectedHighways.map((highway) =>
          withoutVehicleType(highway, vehicleTypeId),
        );
        if (updatedHighways.includes(null)) {
          snackbarSvc.showMessage(
            t(
              'interface:properties.area.supported_vehicle.not_allowed_to_deselect_vehicle_below_amount',
              `Can not deselect vehicle. Minimum amount: ${SELECTABLE_VEHICLE_AMOUNT_PER_AREA.MIN}`,
              { amount: SELECTABLE_VEHICLE_AMOUNT_PER_AREA.MIN },
            ),
          );
          return;
        }

        updatedHighways.forEach((highway) => {
          if (highway) pushAreaToItsCollection(updatedAreas, highway);
        });
      } else {
        // Select on every highway
        fddmSvc.setPreference('area.vehicleTypeId', vehicleTypeId);
        selectedHighways
          .filter((highway) => !getVehicleTypesByArea(highway).includes(vehicleTypeId))
          .forEach((highway) => {
            pushAreaToItsCollection(updatedAreas, withVehicleType(highway, vehicleTypeId));
          });
      }

      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, isMixed, overrideVehicleType, selectedHighways, selectedOptions, snackbarSvc, t],
  );

  return (
    <>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.supported_vehicle.label'>
          <Stack sx={containerSx}>
            <MultiSelectInput
              disabled={isDisabled}
              options={options}
              selectedOptions={selectedOptions}
              onItemClick={onItemClick}
              displayValue={displayValue}
              showMixed={isMixed}
            />
          </Stack>
        </PropertiesLabel>
      </div>
    </>
  );
};

export const MultiVehicleType = memo(MultiVehicleTypeComponent);
